import { DatePipe, NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ClusterNode, Edge, GraphModule, Layout, Node } from '@swimlane/ngx-graph';
import * as d3 from 'd3';
import { NzBreadCrumbComponent, NzBreadCrumbItemComponent } from 'ng-zorro-antd/breadcrumb';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzDescriptionsComponent, NzDescriptionsItemComponent } from 'ng-zorro-antd/descriptions';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderBreadcrumbDirective, NzPageHeaderComponent, NzPageHeaderContentDirective, NzPageHeaderTagDirective } from 'ng-zorro-antd/page-header';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { ConfigurationDto } from 'src/app/shared/dto/configuration.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { ProductionOrderDto } from 'src/app/shared/dto/production-order.dto';
import { ConfigurationTypesEnum } from 'src/app/shared/enums/configuration-types.enum';
import { ProductionOrderStatesEnum } from 'src/app/shared/enums/production-order-states.enum';
import { ConfigurationService } from '../../services/configuration.service';
import { ProductionOrderService } from '../../services/production-order.service';
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
    FileDoneOutline,
    FundViewOutline
} from '@ant-design/icons-angular/icons';
import { IconDefinition } from '@ant-design/icons-angular';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { showDialog } from 'src/app/shared/utils/utils';
import { NzModalService } from 'ng-zorro-antd/modal';
import _ from 'lodash';
import { ProductionOrderProcessesService } from '../../services/production-order-processes.service';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { StatusEnum } from 'src/app/shared/enums/production-order-state-ids.enum';
import { NzProgressComponent } from 'ng-zorro-antd/progress';

const icons: IconDefinition[] = [
    FundViewOutline,
    FileDoneOutline
];

@Component({
    selector: 'app-production-order-detail',
    templateUrl: './production-order-detail.component.html',
    styleUrl: './production-order-detail.component.scss',
    standalone: true,
    imports: [
        NzPageHeaderComponent,
        NzButtonComponent,
        NzTableModule,
        NzWaveDirective,
        ɵNzTransitionPatchDirective,
        NzPageHeaderTagDirective,
        NzBreadCrumbComponent,
        NzPageHeaderBreadcrumbDirective,
        NzBreadCrumbItemComponent,
        RouterLink,
        NzPageHeaderContentDirective,
        NzDescriptionsComponent,
        NzDescriptionsItemComponent,
        NzCheckboxComponent,
        ReactiveFormsModule,
        FormsModule,
        GraphModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NgClass,
        DatePipe,
        NzMessageModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzDividerComponent,
        NzProgressComponent
    ]
})

export class ProductionOrderDetailComponent extends DefaultComponent implements OnInit, OnDestroy {
    productionOrderId!: number;
    isLoading: boolean = false;
    order!: ProductionOrderDto;
    nodes: Node[] = [];
    links: Edge[] = [];

    idColumnValueToShow: string = 'id';
	orderTitle: string = '';
	idValueToShow: string = '';

    // Graph properties
    clusters: ClusterNode[] = [];
    layout: string | Layout = 'dagreCluster';
    curveType: string = 'Bundle';
    curve: any = d3.curveBundle;
    draggingEnabled: boolean = false;
    panningEnabled: boolean = true;
    zoomEnabled: boolean = false;
    zoomSpeed: number = 0.1;
    minZoomLevel: number = 0.1;
    maxZoomLevel: number = 4.0;
    panOnZoom: boolean = true;
    autoZoom: boolean = true;
    autoCenter: boolean = true;
    center$: Subject<boolean> = new Subject();

    configurations: ConfigurationDto[] = [];
    userLogged!: UserDto;

    private readonly activeRoute = inject(ActivatedRoute);
    private readonly configurationService = inject(ConfigurationService);
    private readonly productionOrderService = inject(ProductionOrderService);
    private readonly productionOrderProcessesService = inject(ProductionOrderProcessesService);
    private readonly router = inject(Router);
    private readonly message = inject(NzMessageService);
    private readonly iconService = inject(NzIconService);
    private readonly modalService = inject(NzModalService);
    private readonly userLoggedService = inject(UserLoggedService);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('productionOrderDetail', '');
        this.productionOrderId = this.activeRoute.snapshot.params['idOrder'];
        this.iconService.addIcon(...icons);
        this.userLogged = _.cloneDeep(this.userLoggedService.userLogged);
    }

    async ngOnInit(): Promise<void> {
        try {
            this.isLoading = true;

            await this.productionOrderService.setupWebSocketById(this.productionOrderId);

            this.productionOrderService.productionOrder$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: async (productionOrder) => {

                        if (!productionOrder) {
                            this.isLoading = false;
                            throw new Error(`No se ha encontrado la orden ${this.productionOrderId}`);
                        }

                        this.order = _.cloneDeep(productionOrder);

                        this.buildForkEdges();
                    },
                    error: () => {
                        this.message.error('Error cargando la orden de producción');
                    }
                });

            this.configurations = await this.configurationService.getConfigurations();

			this.idColumnValueToShow = (await this.configurationService.getConfigurationByKey('order.idColumnValue')).value.toString() ?? 'id';

			this.orderTitle = this.getNzTitle();

            this.isLoading = false;
        } catch (error: any) {
            this.message.error(error.message);
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.productionOrderService.disconnectWebSocketById(this.productionOrderId);
    }

    navigateToOperacion(idProcess: number): void {
        const newTabConfig: ConfigurationDto | undefined = this.configurations.find(
            (c) => c.key === 'productionOrderProcessesDetail.process.openInNewTab'
        );

        if (newTabConfig != null && newTabConfig.type === ConfigurationTypesEnum.BOOLEAN) {
            const newTagConfigValue: boolean = JSON.parse(newTabConfig.value as string);

            if (newTagConfigValue) {
                window.open(`production-orders/${this.productionOrderId}/process/${idProcess}`);
                return;
            }
        }

        this.router.navigate([`production-orders/${this.productionOrderId}/process/${idProcess}`]);
    }

    buildForkEdges() {
        this.setInterpolationType(this.curveType);

        for (const productionProcess of this.order?.productionOrderProcesses ?? []) {
            //TODO: qtp
            // const label: string = `${productionProcess.productionProcess.name} - ${productionProcess.quantityProduced} / ${productionProcess.quantityToProduce}`;
            const label: string = `${productionProcess.productionProcess.name}`;
            this.nodes.push({
                id: `${productionProcess.id}`,
                label: label,
                data: {
                    productionOrderId: this.order?.id,
                    productionProcessId: productionProcess.id,
                    //TODO: qtp
                    quantity: 0,
                    colors: this.getColorsSVG(productionProcess.status!.id)
                },
                dimension: {
                    height: 30,
                    width: label.length * 9
                }
            });
        }

        for (const forkEdge of this.order?.forkEdges ?? []) {
            this.links.push({
                id: `link${forkEdge.id?.toString()}`,
                source: forkEdge.parentProcessId?.toString(),
                target: forkEdge.childProcessId?.toString(),
                label: forkEdge.quantity?.toString()
            });
        }
    }

    calculateMillisToDateTime(millisec: number) {
        let seconds: number = +(millisec / 1000).toFixed(0);
        let minutes: number = Math.floor(seconds / 60);
        let hours: number = 0;

        if (minutes > 59) {
            hours = Math.floor(minutes / 60);
            minutes = minutes - hours * 60;
        }

        seconds = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours >= 10 ? '' : '0'}${hours} : ${minutes >= 10 ? '' : '0'}${minutes} : ${seconds >= 10 ? '' : '0'}${seconds}`;
        }

        return `${minutes >= 10 ? '' : '0'}${minutes} : ${seconds >= 10 ? '' : '0'}${seconds}`;
    }

    getTotalTime(process: ProductionOrderProcessDto) {
        let milisecPauses: number = 0;

        for (let i = 0; i < process.productionPauses!.length; i++) {
            const pause = process.productionPauses![i];
            milisecPauses += new Date(pause.endDate).getTime() - new Date(pause.startDate).getTime();
        }

        const millisec: number = new Date(process.productionTime!.endDate).getTime() - new Date(process.productionTime!.startDate).getTime();
        const millisecToUse = millisec - milisecPauses;

        return this.calculateMillisToDateTime(millisecToUse);
    }

    // FORKS
    setInterpolationType(curveType: string) {
        this.curveType = curveType;


        if (curveType === 'Bundle') {
            this.curve = d3.curveBundle.beta(1);
        }
        if (curveType === 'Cardinal') {
            this.curve = d3.curveCardinal;
        }
        if (curveType === 'Catmull Rom') {
            this.curve = d3.curveCatmullRom;
        }
        if (curveType === 'Linear') {
            this.curve = d3.curveLinear;
        }
        if (curveType === 'Monotone X') {
            this.curve = d3.curveMonotoneX;
        }
        if (curveType === 'Monotone Y') {
            this.curve = d3.curveMonotoneY;
        }
        if (curveType === 'Natural') {
            this.curve = d3.curveNatural;
        }
        if (curveType === 'Step') {
            this.curve = d3.curveStep;
        }
        if (curveType === 'Step After') {
            this.curve = d3.curveStepAfter;
        }
        if (curveType === 'Step Before') {
            this.curve = d3.curveStepBefore;
        }
    }

    canReadProductionOrderProcessDetail(){
        return this.permissionService.canRead('productionOrderProcessDetail', '');
    }

    async forceFinishProduction(productionOrderProcess:ProductionOrderProcessDto){
        if (await showDialog(this.modalService, `Vas a finalizar el proceso ${productionOrderProcess.productionProcess.name}. Esta acción no se puede revertir, ¿deseas continuar con la finalización del proceso?`)) {
            try {
                //TODO: REVIEW WHEN FORCE UPDATE FUNCTION FIXED
                await this.productionOrderProcessesService.setProductionProcessAsFinished(this.productionOrderId, productionOrderProcess.id!, this.userLogged.id);
                this.message.success(`Proceso ${productionOrderProcess.productionProcess.name} finalizado correctamente`);
            } catch (error: any) {
                this.message.error(error.message);
            }
        }
    }

    calculateProcessProgress(productionOrderProcess: ProductionOrderProcessDto): number {
		if (!productionOrderProcess.finalProducts) {
			return 0;
		}

		if (productionOrderProcess.status?.id === StatusEnum.FINISHED) {
			return 100;
		}

		const quantityToProduce = productionOrderProcess.finalProducts.reduce((total, finalProduct) => {
			return total + (finalProduct.quantityToProduce || 0);
		}, 0);

		const quantityProduced = productionOrderProcess.finalProducts.reduce((total, finalProduct) => {
			return total + (finalProduct.quantityProduced || 0);
		}, 0);

		return quantityProduced === 0 || quantityToProduce === 0 ? 0 : Math.round((quantityProduced / quantityToProduce) * 100);
	}

    private getColorsSVG(statusId: number): {
        background: string;
        textColor: string;
    } {
        const attributes = {
            background: '',
            textColor: ''
        };

        switch (statusId) {
            case StatusEnum.FINISHED:
                attributes.background = '#fde8e8';
                attributes.textColor = '#9b1c1c';
                break;
            case StatusEnum.AWAITING:
                attributes.background = '#E1EFFE';
                attributes.textColor = '#1E429F';
                break;
            case StatusEnum.PAUSED:
                attributes.background = '#FDF6B2';
                attributes.textColor = '#723B13';
                break;
            case StatusEnum.IN_PROGRESS:
                attributes.background = '#DEF7EC';
                attributes.textColor = '#03543F';
                break;
            default:
                attributes.background = '#03543f';
                attributes.textColor = '#DEF7EC';
                break;
        }
        return attributes;
    }

    private getNzTitle(): string {
		if (this.idColumnValueToShow === 'id') {
			this.idValueToShow = this.productionOrderId.toString();
		} else if (this.idColumnValueToShow === 'externalId') {
			this.idValueToShow = this.order.externalId?.toString() ?? '';
		} else if (this.idColumnValueToShow === 'finalProduct') {
			this.idValueToShow = this.getIdColumnValue();
		} else {
			this.idValueToShow = '-';
		}

		return `Orden ${this.idValueToShow} -`;
	}

	private getIdColumnValue(): string {
		return (
			(this.order.productionOrderProcesses?.length &&
				this.order.productionOrderProcesses[this.order.productionOrderProcesses.length - 1]?.finalProducts?.[0]?.articleBatch?.batchNumber) ||
			''
		);
	}
}
